import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import {
  Role,
  Module,
  Permission,
  PERMISSIONS,
  ROLE_CONFIG,
} from '@/config/permissions';

interface PermissionMatrixProps {
  role: Role;
  onChange?: (role: Role, module: Module, permission: Permission, enabled: boolean) => void;
  readOnly?: boolean;
}

const MODULE_LABELS: Record<Module, string> = {
  dashboard: 'Dashboard',
  accounts: 'Accounts',
  contacts: 'Contacts',
  abm_journey: 'ABM Journey',
  call_management: 'Call Management',
  mql_gate: 'MQL Gate',
  magnets: 'Magnets',
  users: 'Users',
  prospecting: 'Prospecting',
  reports: 'Reports',
};

const PERMISSION_LABELS: Record<Permission, string> = {
  read: 'Read',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  schedule: 'Schedule',
  approve: 'Approve',
  reject: 'Reject',
};

const ALL_PERMISSIONS: Permission[] = ['read', 'create', 'edit', 'delete', 'schedule', 'approve', 'reject'];

export default function PermissionMatrix({ role, onChange, readOnly = false }: PermissionMatrixProps) {
  const [expandedModules, setExpandedModules] = useState<Module[]>([]);

  const toggleModule = (module: Module) => {
    setExpandedModules(prev =>
      prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module]
    );
  };

  const hasPermission = (module: Module, permission: Permission): boolean => {
    return PERMISSIONS[role][module]?.includes(permission) || false;
  };

  const getPermissionCount = (module: Module): { granted: number; total: number } => {
    const modulePerms = PERMISSIONS[role][module] || [];
    // Only count relevant permissions (not approve/reject for non-MQL modules)
    const relevantPerms = module === 'mql_gate' 
      ? ALL_PERMISSIONS 
      : module === 'call_management'
      ? ['read', 'create', 'edit', 'delete', 'schedule']
      : ['read', 'create', 'edit', 'delete'];
    
    return {
      granted: relevantPerms.filter(p => modulePerms.includes(p)).length,
      total: relevantPerms.length,
    };
  };

  const getPermissionColor = (module: Module): string => {
    const { granted, total } = getPermissionCount(module);
    if (granted === 0) return 'bg-red-50 border-red-200';
    if (granted === total) return 'bg-emerald-50 border-emerald-200';
    return 'bg-amber-50 border-amber-200';
  };

  const getPermissionBadgeColor = (module: Module): string => {
    const { granted, total } = getPermissionCount(module);
    if (granted === 0) return 'bg-red-100 text-red-800 border-red-200';
    if (granted === total) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    return 'bg-amber-100 text-amber-800 border-amber-200';
  };

  return (
    <div className="space-y-4">
      {/* Role Badge */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-600">Current Role:</span>
        <Badge className={`${ROLE_CONFIG[role].color} border text-sm px-3 py-1`}>
          {ROLE_CONFIG[role].label}
        </Badge>
      </div>

      {/* Permission Cards */}
      <div className="space-y-3">
        {(Object.keys(MODULE_LABELS) as Module[]).map(module => {
          const { granted, total } = getPermissionCount(module);
          const isExpanded = expandedModules.includes(module);

          return (
            <Card key={module} className={`border ${getPermissionColor(module)} overflow-hidden`}>
              {/* Module Header */}
              <button
                onClick={() => toggleModule(module)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{MODULE_LABELS[module]}</span>
                  <Badge className={`${getPermissionBadgeColor(module)} border text-xs`}>
                    {granted} / {total} permissions
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {granted === 0 && (
                    <span className="text-xs text-red-600 font-medium">No Access</span>
                  )}
                  {granted > 0 && granted < total && (
                    <span className="text-xs text-amber-600 font-medium">Partial Access</span>
                  )}
                  {granted === total && (
                    <span className="text-xs text-emerald-600 font-medium">Full Access</span>
                  )}
                  <span className="text-gray-400">{isExpanded ? '▼' : '▶'}</span>
                </div>
              </button>

              {/* Permission Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200 bg-white">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-4">
                    {ALL_PERMISSIONS.map(permission => {
                      const hasAccess = hasPermission(module, permission);
                      const isRelevant = 
                        (permission === 'schedule' && module === 'call_management') ||
                        ((permission === 'approve' || permission === 'reject') && module === 'mql_gate') ||
                        (['read', 'create', 'edit', 'delete'].includes(permission));

                      if (!isRelevant) {
                        return (
                          <div key={permission} className="flex flex-col items-center justify-center p-3 rounded bg-gray-50 opacity-50">
                            <span className="text-xs text-gray-400 mb-2">{PERMISSION_LABELS[permission]}</span>
                            <div className="w-5 h-5 rounded border border-gray-300 bg-gray-100 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">N/A</span>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={permission}
                          className={`flex flex-col items-center justify-center p-3 rounded border transition-all ${
                            hasAccess
                              ? 'bg-emerald-50 border-emerald-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <span className="text-xs font-medium text-gray-700 mb-2">
                            {PERMISSION_LABELS[permission]}
                          </span>
                          <div
                            className={`w-6 h-6 rounded flex items-center justify-center ${
                              hasAccess
                                ? 'bg-emerald-600'
                                : 'bg-gray-300'
                            }`}
                          >
                            {hasAccess ? (
                              <Check className="w-4 h-4 text-white" />
                            ) : (
                              <X className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Legend */}
      <Card className="p-4 bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-6 text-sm">
          <span className="font-medium text-gray-700">Legend:</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-600"></div>
            <span className="text-gray-600">Granted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-300"></div>
            <span className="text-gray-600">Denied</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border border-gray-300 bg-gray-100"></div>
            <span className="text-gray-600">Not Applicable</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
